import { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import ImpersonateReasonDialog from "@/cleanup/containers/Impersonate/ImpersonateReasonDialog";
import {
  DangerTag,
  SecondaryTag,
  SuccessTag,
  WarningTag,
} from "@cleanup/components/TagMore";
import {
  UserBouncedTag,
  UserPendingTag,
  UserExpiredTag,
} from "@cleanup/components/UserTags/UserTags";
import moment from "@/constants/SensolusMoment";
import * as actions from "@/redux/actions";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

import userClient from "@apis/userClient";

export enum ImpersonateType {
  VIEWER = "VIEWER",
  ADMIN = "ADMIN",
  IMPERSONATE = "IMPERSONATE",
}

interface User {
  status?: string;
  emailBounce?: boolean;
  emailVerified?: boolean;
  expirationDate?: string | null;
}

interface ImpersonateUserParams {
  id: string | number;
  type: ImpersonateType;
  ignoreCurrentContext?: boolean;
  onBadRequest?: (error: unknown) => void;
  /**
   * Required for {@link ImpersonateType.IMPERSONATE}. If omitted on a user
   * impersonation, the hook will open a dialog asking the caller for one
   * before sending the request.
   */
  reason?: string;
}

interface UseUserReturn {
  getPendingAndEmailStatusLabel: (user: User) => JSX.Element;
  getUserStatusLabel: (user: User) => JSX.Element | string;
  getEmailStatusLabel: (user: User) => JSX.Element;
  onImpersonateUser: (params: ImpersonateUserParams) => void;
  IMPERSONATE_TYPE: typeof ImpersonateType;
  /**
   * Must be rendered somewhere inside the component that uses
   * {@link onImpersonateUser}. The dialog is only shown for user-impersonation
   * when no reason was passed.
   */
  ImpersonateReasonDialogRoot: React.FC;
}

const useUser = (): UseUserReturn => {
  const history = useHistory();
  const dispatch = useDispatch();

  const getPendingAndEmailStatusLabel = (user: User): JSX.Element => {
    const pendingText = translate(LanguageKey.pending_key);
    const bouncedText = translate(LanguageKey.bounced_key);
    const expiredText = translate(LanguageKey.invitation_expired_key);
    const notVerifiedText = translate(LanguageKey.not_verified_key);
    const okText = translate(LanguageKey.ok_key);

    if (user.status === "PENDING") {
      if (user.emailBounce) {
        return (
          <>
            <UserPendingTag tagName={pendingText} />
            <UserBouncedTag className="mt-1" tagName={bouncedText} />
          </>
        );
      }
      return <UserPendingTag tagName={pendingText} />;
    } else if (user.status === "INVITATION_EXPIRED") {
      if (user.emailBounce) {
        return (
          <>
            <UserExpiredTag tagName={expiredText} />
            <UserBouncedTag className="mt-1" tagName={bouncedText} />
          </>
        );
      }
      return <UserExpiredTag tagName={expiredText} />;
    } else if (user.emailBounce && !user.emailVerified) {
      return (
        <>
          <UserBouncedTag tagName={bouncedText} />
          <WarningTag className="mt-1" tagName={notVerifiedText} />
        </>
      );
    } else if (user.emailBounce) {
      return <UserBouncedTag tagName={bouncedText} />;
    } else if (!user.emailVerified) {
      return <WarningTag tagName={notVerifiedText} />;
    } else {
      return <SuccessTag tagName={okText} />;
    }
  };

  const getEmailStatusLabel = (user: User): JSX.Element => {
    const bouncedText = translate(LanguageKey.bounced_key);
    const notVerifiedText = translate(LanguageKey.not_verified_key);
    const okText = translate(LanguageKey.ok_key);

    if (user.emailBounce && !user.emailVerified) {
      if (user.status !== "PENDING" && user.status !== "INVITATION_EXPIRED") {
        return (
          <>
            <UserBouncedTag tagName={bouncedText} />
            <WarningTag className="mt-1" tagName={notVerifiedText} />
          </>
        );
      } else {
        return <UserBouncedTag tagName={bouncedText} />;
      }
    } else if (user.emailBounce) {
      return <UserBouncedTag tagName={bouncedText} />;
    } else if (!user.emailVerified && user.status !== "PENDING") {
      return <WarningTag tagName={notVerifiedText} />;
    } else {
      return <SuccessTag tagName={okText} />;
    }
  };

  const getUserStatusLabel = (user: User): JSX.Element | string => {
    const pendingText = translate(LanguageKey.pending_key);
    const expiredText = translate(LanguageKey.invitation_expired_key);
    const expiredUpperText = translate(LanguageKey.expired_upper_key);
    const lockedText = translate(LanguageKey.locked_key);
    const activeText = translate(LanguageKey.active_key);

    if (user.status === "PENDING") {
      return <UserPendingTag tagName={pendingText} />;
    } else if (user.status === "INVITATION_EXPIRED") {
      return <UserExpiredTag tagName={expiredText} />;
    } else if (isBeforeDateNow(user.expirationDate)) {
      return <SecondaryTag tagName={expiredUpperText} />;
    } else if (user.status === "LOCKED") {
      return <DangerTag tagName={lockedText} />;
    } else if (user.status === "ACTIVE") {
      return <SuccessTag tagName={activeText} />;
    } else {
      return "";
    }
  };

  const isBeforeDateNow = (date?: string | null): boolean => {
    if (date != null) {
      const _date = moment(date);
      const now = moment();
      if (_date.isBefore(now)) {
        return true;
      }
    }
    return false;
  };

  const [pendingImpersonation, setPendingImpersonation] =
    useState<ImpersonateUserParams | null>(null);

  const performImpersonation = ({
    id,
    type,
    ignoreCurrentContext = false,
    onBadRequest,
    reason,
  }: ImpersonateUserParams): void => {
    let call: Promise<unknown>;

    if (type === ImpersonateType.ADMIN) {
      call = userClient.impersonateAdminSupportUserByOrgId(id as number);
    } else if (type === ImpersonateType.IMPERSONATE) {
      call = userClient.impersonateUser(id as string, reason ?? "");
    } else {
      call = userClient.impersonateViewerSupportUserByOrgId(id as number);
    }

    call
      .then(() => {
        dispatch(actions.preventSidebar(true)); //store in redux

        const bc = new BroadcastChannel("impersonate_reload");
        bc.postMessage(
          "reload the tabs."
        ); /* send reloading event, msg content doesn't matter */
        bc.close();

        if (type !== ImpersonateType.ADMIN || ignoreCurrentContext) {
          history.push("/");
        }
      })
      .catch((error: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const err = error as any;
        if (err?.response?.status === 400 && onBadRequest) {
          onBadRequest(error);
        } else {
          history.push("/login");
        }
      });
  };

  const onImpersonateUser = (params: ImpersonateUserParams): void => {
    // For user impersonation we require a reason; if none was supplied we
    // open the audit-reason dialog and defer the call until the user confirms.
    if (params.type === ImpersonateType.IMPERSONATE && !params.reason) {
      setPendingImpersonation(params);
      return;
    }
    performImpersonation(params);
  };

  const ImpersonateReasonDialogRoot: React.FC = () => (
    <ImpersonateReasonDialog
      isShow={pendingImpersonation !== null}
      onCancel={() => setPendingImpersonation(null)}
      onConfirm={(reason) => {
        if (pendingImpersonation) {
          performImpersonation({ ...pendingImpersonation, reason });
        }
        setPendingImpersonation(null);
      }}
    />
  );

  const IMPERSONATE_TYPE = ImpersonateType;

  return {
    getPendingAndEmailStatusLabel,
    getUserStatusLabel,
    getEmailStatusLabel,
    onImpersonateUser,
    IMPERSONATE_TYPE,
    ImpersonateReasonDialogRoot,
  };
};

export default useUser;
